import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  Container,
  Input,
  Label,
  Button,
} from "../../Components/Common/Tailwind";
import {
  AccordionItem,
  AccordionHeader,
  AccordionBody,
  UncontrolledAccordion,
} from "@/Components/Common/Tailwind";
import Navbar from "../../Components/Common/navbar";
import QuantityButtonInput from "../../Components/Common/QuantityButtonInput";
import { packageService } from "../../api/services/package.service";
import MobileViewContainer from "../../Components/MobileViewContainer";
import {
  calculateAddOptionsTotal,
  calculateCancelOptionsTotal,
  getCancelUnavailableReason,
  getSelectedAddOptions,
  isCustomerCancelableOption,
} from "./addOptionsSelection.mjs";

const AddOptionsDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [optionProducts, setOptionProducts] = useState([]);
  const [addOptionQuantities, setAddOptionQuantities] = useState({});
  const [cancelOptionIds, setCancelOptionIds] = useState([]);
  const [reservationData, setReservationData] = useState(null);
  const [optionOpen, setOptionOpen] = useState("1");
  const [isCancelingOptions, setIsCancelingOptions] = useState(false);

  const optionToggle = (nextId) => {
    if (optionOpen === nextId) {
      setOptionOpen();
      return;
    }

    setOptionOpen(nextId);
  };

  const fetchReservationData = async () => {
    try {
      const reservationResponse = await packageService.getReservationById(id);
      setReservationData(reservationResponse.data);
      setCancelOptionIds([]);

      const packageResponse = await packageService.getPackageItemById(reservationResponse.data.package_id);
      setOptionProducts(packageResponse.data.options || []);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (id) {
      fetchReservationData();
    }

    setOptionProducts([]);
  }, [id]);

  const existingOptions = reservationData?.options || [];
  const selectedAddOptions = getSelectedAddOptions(optionProducts, addOptionQuantities, reservationData?.id);
  const addOptionsTotal = calculateAddOptionsTotal(selectedAddOptions);
  const cancelOptionsTotal = calculateCancelOptionsTotal(existingOptions, cancelOptionIds);

  const handleCancelOptionToggle = (checked, optionId) => {
    setCancelOptionIds((prev) => {
      if (checked) return [...new Set([...prev, optionId])];
      return prev.filter((idValue) => String(idValue) !== String(optionId));
    });
  };

  const handleAddOptionQuantity = (optionItem, quantity) => {
    if (optionItem.type === "extra" && quantity > reservationData.max_personnel) {
      toast.error(`최대 ${reservationData.max_personnel}개까지 선택 가능합니다.`);
      return;
    }

    setAddOptionQuantities((prev) => ({
      ...prev,
      [optionItem.id]: Math.max(0, quantity),
    }));
  };

  const cancelSelectedOptions = async () => {
    if (!cancelOptionIds.length || isCancelingOptions) return;

    const confirmed = window.confirm(`선택한 옵션 ${cancelOptionIds.length}개를 취소하시겠습니까?`);
    if (!confirmed) return;

    setIsCancelingOptions(true);
    try {
      const response = await packageService.cancelReservationOptionsByIds(cancelOptionIds);
      if (response.data?.result === "ok") {
        toast.success("선택한 옵션이 취소되었습니다.");
        await fetchReservationData();
      } else {
        toast.error(response.data?.error || "옵션 취소 요청이 처리되지 않았습니다.");
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.message || "옵션 취소 요청이 처리되지 않았습니다.");
    } finally {
      setIsCancelingOptions(false);
    }
  };

  return (
    <React.Fragment>
      <MobileViewContainer>
        <Navbar />
        <Container fluid style={{ minHeight: "100vh", backgroundColor: "white", paddingBottom: "400px" }}>
          <Card>
            <div className="px-4 py-2">
              {reservationData ? (
                <CardBody className="px-0">
                  <div className="mt-xl-2 mt-5">
                    <div className="flex flex-col">
                      <div className="flex-1">
                        <h4 style={{ fontFamily: "Gmarket-M" }}>{reservationData["name"]}</h4>
                      </div>
                      <div className="mt-2">
                        <div className="mx-2">자리 {reservationData["spot"]}</div>
                        <div className="mx-2">예약시작일 {reservationData["reservationDate"]}</div>
                      </div>
                    </div>

                    {reservationData.options && reservationData.options.length ? (
                      <div className="mt-5">
                        <div className="mb-2">
                          <h5>기존 옵션</h5>
                        </div>
                        <div className="space-y-2">
                          {reservationData.options.map((optionItem, idx) => {
                            const canCancel = isCustomerCancelableOption(optionItem);
                            const disabledReason = getCancelUnavailableReason(optionItem);

                            return (
                              <div
                                key={optionItem.id || idx}
                                className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2"
                              >
                                <div className="form-check my-1" dir="ltr">
                                  <Input
                                    type="checkbox"
                                    className="form-check-input"
                                    id={`${reservationData.id}-${idx}-old`}
                                    checked={cancelOptionIds.some((optionId) => String(optionId) === String(optionItem.id))}
                                    disabled={!canCancel || isCancelingOptions}
                                    onChange={(event) => {
                                      handleCancelOptionToggle(event.currentTarget.checked, optionItem.id);
                                    }}
                                  />
                                  <Label for={`${reservationData.id}-${idx}-old`} style={{ fontSize: "12px" }}>
                                    {optionItem.name} <br />+{Number(optionItem.price || 0).toLocaleString()}원
                                  </Label>
                                </div>
                                {!canCancel ? (
                                  <span className="text-xs text-gray-500 text-right">{disabledReason}</span>
                                ) : null}
                              </div>
                            );
                          })}
                          {cancelOptionIds.length ? (
                            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
                              취소 선택 {cancelOptionIds.length}개 / 환불 예정 {cancelOptionsTotal.toLocaleString()}원
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ) : null}

                    <div className="mt-5">
                      <UncontrolledAccordion open={optionOpen} toggle={optionToggle} defaultOpen="1">
                        <AccordionItem>
                          <AccordionHeader targetId="1" style={{ fontFamily: "Gmarket-M" }}>
                            추가 옵션
                          </AccordionHeader>

                          <AccordionBody accordionId="1">
                            {optionProducts.map((optionItem, idx) => (
                              <div
                                key={optionItem.id || idx}
                                className="my-2 flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2"
                              >
                                <div className="min-w-0 flex-1 pr-2" style={{ fontSize: "12px", fontFamily: "Gmarket-M", color: "#1d1d1d" }}>
                                  {optionItem.name} <br />+{Number(optionItem.price || 0).toLocaleString()}원
                                </div>
                                <div className="shrink-0">
                                  <QuantityButtonInput
                                    quantity={addOptionQuantities[optionItem.id] || 0}
                                    detailPage
                                    minQuantity={0}
                                    variant="subtle"
                                    onChange={(value) => {
                                      handleAddOptionQuantity(optionItem, value);
                                    }}
                                  />
                                </div>
                              </div>
                            ))}
                          </AccordionBody>
                        </AccordionItem>
                      </UncontrolledAccordion>
                    </div>
                  </div>
                </CardBody>
              ) : (
                <div>loading...</div>
              )}
            </div>
          </Card>

          <div
            className="fixed flex flex-row justify-center"
            style={{
              maxWidth: "640px",
              bottom: "0px",
              width: "100%",
              zIndex: 999,
              backgroundColor: "#fff",
              paddingBottom: "25px",
              paddingTop: "25px",
              alignItems: "center",
              borderTop: "1px solid #ededed",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            <div className="flex w-full flex-col items-center gap-2">
              {selectedAddOptions.length > 0 ? (
                <div style={{ maxHeight: "200px", overflowY: "scroll", width: "100%" }}>
                  {selectedAddOptions.map((optionItem) => (
                    <div
                      key={`reservationOptions${optionItem.optionId}`}
                      style={{
                        width: "95%",
                        padding: "20px 20px",
                        margin: "8px auto",
                        borderRadius: "10px",
                        borderBottom: "1px solid #ededed",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        backgroundColor: "#f4f4f4",
                      }}
                    >
                      <div className="min-w-0 flex-1 pr-2">
                        {optionItem.name}
                      </div>

                      <div className="shrink-0">
                        <QuantityButtonInput
                          quantity={optionItem.quantity}
                          detailPage
                          minQuantity={0}
                          variant="subtle"
                          onChange={(value) => {
                            handleAddOptionQuantity({ ...optionItem, id: optionItem.optionId }, value);
                          }}
                        />
                      </div>
                    </div>
                  ))}
                  <div className="px-4 pb-2 text-right text-sm font-semibold">
                    추가 구매 합계 {addOptionsTotal.toLocaleString()}원
                  </div>
                </div>
              ) : null}
              {cancelOptionIds.length ? (
                <Button
                  className="font-bold text-base"
                  style={{
                    width: "95%",
                    backgroundColor: "#fff",
                    color: "#F6475F",
                    border: "1px solid #F6475F",
                    padding: "12px 0px",
                    borderRadius: "12px",
                    fontFamily: "Gmarket-M",
                  }}
                  size="lg"
                  onClick={cancelSelectedOptions}
                  disabled={isCancelingOptions}
                >
                  {isCancelingOptions ? "취소 처리중..." : "선택 옵션 취소"}
                </Button>
              ) : null}
              <Button
                className="font-bold text-base"
                style={{
                  width: "95%",
                  backgroundColor: "#F6475F",
                  border: "none",
                  padding: "14px 0px",
                  borderRadius: "12px",
                  fontFamily: "Gmarket-M",
                }}
                size="lg"
                onClick={() => {
                  navigate("/add-options-order", {
                    state: {
                      reservationName: reservationData["name"],
                      spotName: reservationData["spot"],
                      reservDate: reservationData["reservationDate"],
                      reservId: reservationData.id,
                      options: selectedAddOptions,
                    },
                  });
                }}
                disabled={!selectedAddOptions.length}
              >
                구매하기
              </Button>
            </div>
          </div>
        </Container>
      </MobileViewContainer>
    </React.Fragment>
  );
};

export default AddOptionsDetail;
